import React, {useCallback, useEffect, useMemo, useState} from 'react';
import SecurSpaceContainer from "../../../components/common/SecurSpaceContainer";
import {Grid, Typography} from "@mui/material";
import moment from "moment";
import {DateFormats} from "../../../components/constants/securspace-constants";
import InvoiceList from "../../../components/admin/invoices/redesign/InvoiceList";
import InvoiceSortFilter from "../../../components/admin/invoices/redesign/InvoiceSortFilter/InvoiceSortFilter";
import { ADMIN_INVOICES_DATE_BYPASS_KEYS } from "../../../components/admin/invoices/redesign/AdminInvoicesQueryType";
import InvoiceActionsDialogWrapper
  from "../../../components/admin/invoices/redesign/InvoiceDialogs/InvoiceActionsDialogWrapper";
import Pagination from "../../../components/deprecatedUikit/Pagination/Pagination";
import {requestAdminInvoices} from "../../../requests/reports/invoices/admin-invoice-requests";
import InvoiceGroupSummary from "../../../components/admin/invoices/redesign/InvoiceGroupSummary";
import ExportCSVs from "../../../components/admin/invoices/redesign/ExportCSVs";
import {getErrorMessageForNonStandardAndStandardResponse} from "../../../util/NetworkErrorUtil";
import {CallbackActions} from "../../../util/ActionUtils";
import useUrlQueryParams from "../../../hooks/useUrlQueryParams";
import {useSnackbar} from "../../../context/snackbar-context";
import {SecurSpaceReportContainer} from "../../../components/common";
import {withRemountOnQueryChange} from "../../../components/hocs/withRemountOnQueryChange";
import {SupplierInvoiceStatusGroup} from "../../../components/constants/booking-transaction-constants";
import {
  APPLY_DATES_PARAM,
  parseDateRangeParam,
  withDateRangeBypass
} from "../../../util/ReportDateRangeUtils";
import {usePersistDateRangeParams} from "../../../hooks/report/usePersistDateRangeParams";

const ACCOUNT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;


/**
 * This component will be refactored/replaced to use bound url query params. No need to migrate to TS.
 * @param account
 * @return {React.JSX.Element}
 */
const AdminInvoicesReport = ({account}) => {

  const { snackbarShowMessage } = useSnackbar();
  const urlParams = useUrlQueryParams();
  // Links built with the API's field names (e.g. from the assistant) are accepted too.
  const urlBookingNumber = urlParams.bookingNumber || urlParams.orderNumber || "";
  const urlInvoiceNumber = urlParams.invoiceNumber || urlParams.transactionNumber || "";
  // A link to an older invoice widens the window, or the list opens empty. A blank date is All Dates.
  const urlStartDate = parseDateRangeParam(urlParams.startDate);
  const urlEndDate = parseDateRangeParam(urlParams.endDate);
  // Links from Sentry can open the list on one status queue or one company; a
  // value the filters do not offer is ignored rather than sent to the search.
  const urlStatus = Object.prototype.hasOwnProperty.call(SupplierInvoiceStatusGroup, urlParams.supplierTransactionStatus || '')
    ? urlParams.supplierTransactionStatus
    : '';
  const accountIdFromUrl = (value) => (ACCOUNT_ID.test(value || '') ? value : '');

  const defaultDates = useMemo(() => ({
    startDate: moment().subtract(1, 'month').format(DateFormats.DAY),
    endDate: moment().add(1, 'month').format(DateFormats.DAY),
  }), []);
  const persistDateRange = usePersistDateRangeParams(defaultDates);

  const defaultQuery = {
    startDate: urlStartDate ?? defaultDates.startDate,
    endDate: urlEndDate ?? defaultDates.endDate,
    applyDates: urlParams[APPLY_DATES_PARAM] === 'true',
    bookingNumber: urlBookingNumber,
    transactionNumber: urlInvoiceNumber,
    assetTypeGroup: '',
    buyerAccountId: accountIdFromUrl(urlParams.buyerAccountId),
    supplierAccountId: accountIdFromUrl(urlParams.supplierAccountId),
    transactionType: '',
    bookingStatus: '',
    supplierTransactionStatus: urlStatus,
    taxStatus: '',
    paymentProcessor: '',
    sortBy: 'TRANSACTION_NUMBER',
    sortDir: 'desc',
    page: 0,
    size: 10,
  };

  const [query, setQuery] = useState({...defaultQuery});
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [totalNumberResults, setTotalNumberResults] = useState(0);
  const [page, setPage] = useState(0);
  const [activeDialog, setActiveDialog] = useState(null);
  const [activeInvoice, setActiveInvoice] = useState(null);
  const requestQuery = useMemo(() => withDateRangeBypass(query, ADMIN_INVOICES_DATE_BYPASS_KEYS), [query]);

  const getAdminInvoices = useCallback(() => {
    setLoading(true);
    requestAdminInvoices({...requestQuery, page})
      .then((response) => {
        const {content, totalPages, count, page} = response.body;
        if (page + 1 > totalPages) {
          // If the page index is greater than the total pages
          // This will refresh the data and show the last page of results
          setPage(totalPages > 0 ? totalPages - 1 : 0);
        }
        setInvoices(content);
        setTotalNumberResults(count);
      })
      .catch((err) => {
        snackbarShowMessage(getErrorMessageForNonStandardAndStandardResponse(err), 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [requestQuery, page, snackbarShowMessage]);

  useEffect(() => {
    getAdminInvoices();
  }, [getAdminInvoices]);

  const onInvoiceAction = (callbackAction, invoice, callBackActionDetail) => {
    switch (callbackAction) {
      case CallbackActions.ERROR:
        snackbarShowMessage(callBackActionDetail, 'error');
        break;
      case CallbackActions.OPEN_DIALOG:
        setActiveInvoice(invoice);
        setActiveDialog(callBackActionDetail);
        break;
      case CallbackActions.SUCCESS:
        getAdminInvoices();
        if (typeof callBackActionDetail === 'string' && callBackActionDetail.trim()) {
          snackbarShowMessage(callBackActionDetail, 'success');
        }
        break;
      default:
        throw new Error('Invalid callback action');
    }
  }

  const onDialogClose = (...callbackActions) => {
    setActiveDialog(null);
    setActiveInvoice(null);
    if (callbackActions?.length)  {
      onInvoiceAction(...callbackActions);
    }
  }

  return (
    <SecurSpaceContainer>
      <InvoiceActionsDialogWrapper
        activeDialog={activeDialog}
        invoice={activeInvoice}
        onInvoiceAction={onInvoiceAction}
        onClose={onDialogClose}
      />
      <SecurSpaceReportContainer container rowGap={4}>
        <Typography variant={'h5'} component={'h1'}>Invoices</Typography>
        <InvoiceSortFilter
          initialQuery={defaultQuery}
          setQuery={setQuery}
          onDateRangeChange={persistDateRange}
        />
        <ExportCSVs query={requestQuery} />
        <InvoiceGroupSummary query={requestQuery} />
        <InvoiceList
          account={account}
          invoices={invoices}
          loading={loading}
          onInvoiceAction={onInvoiceAction}
        />
        <Grid item container justifyContent={'end'}>
          <Pagination
            count={totalNumberResults}
            rowsPerPage={query.size}
            page={page}
            onPageChange={(_event, page) => setPage(page)}
            onRowsPerPageChange={(event) => (setQuery((prevState) => ({...prevState, size: event.target.value})))}
            variant={'tablePagination'}
          />
        </Grid>
      </SecurSpaceReportContainer>
    </SecurSpaceContainer>
  );
};

// InvoiceSortFilter keeps whether its panel is expanded in the URL.
export default withRemountOnQueryChange(AdminInvoicesReport, {ignore: ['showAllFilters']});
